import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';

const LOGICALUNIT_URL = 'U3147';

type LogicalUnitEndpointsType = {
  INITLU: string;
  START: string;
  STOP: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  DAY: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
  EXPORT_TO_TIMESHEET: string;
  FINDPROJECT: string;
  FINDSUBPROJECT: string;
  FINDTASKSFORPROJECT: string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  START: `${LOGICALUNIT_URL}/start`,
  STOP: (id) => `${LOGICALUNIT_URL}/stop/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id) => `${LOGICALUNIT_URL}/delete/${id}`,
  DAY: `${LOGICALUNIT_URL}/day`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id) => `${LOGICALUNIT_URL}/${id}`,
  AUDITS: (id) => `${LOGICALUNIT_URL}/${id}/audits`,
  EXPORT_TO_TIMESHEET: `${LOGICALUNIT_URL}/exportToTimesheet`,
  FINDPROJECT: `${LOGICALUNIT_URL}/findProject`,
  FINDSUBPROJECT: `${LOGICALUNIT_URL}/findSubproject`,
  FINDTASKSFORPROJECT: `${LOGICALUNIT_URL}/findTasksForProject`,
};

export type U3147TimeSessionSource = 'timer' | 'manual';

export interface U3147InitLUEmployee {
  id: string;
  firstName: string;
  lastName: string;
}

export interface U3147RecentProjectItem {
  /** Identifiant projet (favoris / modèle honoraires localStorage). */
  id: string;
  /** Alias interne / rétrocompatibilité si l’API envoie `projectId`. */
  projectId?: string;
  subprojectId?: string | null;
  taskId?: string | null;
  note?: string | null;
  projetNumber?: string;
  projetName?: string;
  customerCode?: string;
  customerName?: string;
  lastUsedAt?: string;
  subProjetNumber?: string;
  subProjetName?: string;
  taskCode?: string;
  taskDescriptionFr?: string;
  taskDescriptionEn?: string;
  /** Libellé tâche U3129 (`task.description` sur le modèle honoraires). */
  taskDescription?: string;
  includeSubProjet?: boolean;
}

export function u3147RecentProjectId(item: U3147RecentProjectItem): string | null {
  const raw = item.id ?? item.projectId;
  if (raw == null) return null;
  const id = String(raw).trim();
  return id || null;
}

/** Identifiant projet depuis le formulaire chrono (autocomplete ou puce récente). */
export function u3147ResolveTimerProjectId(
  project: U3147FindProjectItem | U3147ProjectRef | null | undefined,
): string {
  if (!project || typeof project !== 'object') return '';
  const raw = 'id' in project ? project.id : undefined;
  if (raw == null) return '';
  return String(raw).trim();
}

/** Métadonnées tâche insuffisantes pour afficher « code — description ». */
export function u3147TaskMetadataNeedsEnrich(data: {
  taskId?: string | null;
  taskCode?: string | null;
  taskDescriptionFr?: string | null;
  taskDescriptionEn?: string | null;
  taskDescription?: string | null;
}): boolean {
  const taskId = data.taskId?.trim();
  const taskCode = data.taskCode?.trim();
  if (!taskId && !taskCode) return false;

  if (data.taskDescriptionFr?.trim() || data.taskDescriptionEn?.trim()) return false;

  const stored = data.taskDescription?.trim();
  if (stored) {
    if (stored.includes(' — ')) {
      const [storedCode, ...rest] = stored.split(' — ');
      const storedDesc = rest.join(' — ').trim();
      const code = taskCode || taskId || storedCode.trim();
      if (storedDesc && storedDesc !== code && storedDesc !== storedCode.trim()) {
        return false;
      }
    } else if (stored !== taskCode && stored !== taskId) {
      return false;
    }
  }

  return true;
}

function u3147RecentItemTaskMetadata(item: U3147RecentProjectItem) {
  return {
    taskId: item.taskId,
    taskCode: item.taskCode,
    taskDescriptionFr: item.taskDescriptionFr,
    taskDescriptionEn: item.taskDescriptionEn,
    taskDescription: item.taskDescription,
  };
}

/** Libellé favori U3147 : n° projet / tâche / dernière note. */
export function u3147FormatRecentProjectChipLabel(item: U3147RecentProjectItem, lang: string): string {
  const project =
    item.projetNumber?.trim() ||
    item.projetName?.trim() ||
    u3147RecentProjectId(item) ||
    '';

  const taskLabel = u3147FormatTaskLabel(
    {
      id: item.taskId?.trim() ?? '',
      taskCode: item.taskCode,
      taskDescriptionFr: item.taskDescriptionFr,
      taskDescriptionEn: item.taskDescriptionEn,
      description: item.taskDescription,
    },
    lang,
  );

  const note = item.note?.trim() ?? '';
  return [project, taskLabel, note].filter((part) => part.length > 0).join(' / ');
}

/** Puce sans libellé tâche exploitable (projet API ou modèle U3129 incomplet). */
export function u3147ChipNeedsTaskLabelEnrich(item: U3147RecentProjectItem): boolean {
  if (!u3147RecentProjectId(item)) return false;
  return u3147TaskMetadataNeedsEnrich(u3147RecentItemTaskMetadata(item));
}

/** findTasksForProject exige un sous-projet si le projet inclut des sous-projets (U3129). */
export function u3147ChipCanFetchTasksForEnrich(item: U3147RecentProjectItem): boolean {
  if (!u3147ChipNeedsTaskLabelEnrich(item)) return false;
  if (item.includeSubProjet && !item.subprojectId?.trim()) return false;
  return true;
}

export function u3147RecentProjectRichnessScore(item: U3147RecentProjectItem): number {
  let score = 0;
  if (
    item.taskCode?.trim() ||
    item.taskDescriptionFr?.trim() ||
    item.taskDescriptionEn?.trim() ||
    item.taskDescription?.trim()
  ) {
    score += 8;
  } else if (item.taskId?.trim()) {
    score += 4;
  }
  if (item.note?.trim()) score += 2;
  if (item.lastUsedAt) score += 1;
  return score;
}

/** Complète une puce avec les métadonnées tâche (findTasksForProject), y compris si taskId API absent. */
export function u3147EnrichRecentChipFromTaskList(
  item: U3147RecentProjectItem,
  taskList: U3147TaskItem[],
): U3147RecentProjectItem {
  if (!u3147ChipNeedsTaskLabelEnrich(item)) return item;
  const resolvedId = u3147ResolveTaskIdForRecentApply(item, taskList);
  if (!resolvedId) return item;
  const task = taskList.find((entry) => String(entry.id) === String(resolvedId));
  if (!task) return item;
  const enriched = u3147EnrichRecentProjectWithTask({ ...item, taskId: item.taskId?.trim() || resolvedId }, task);
  return {
    ...enriched,
    taskDescription: u3147FormatTaskLabel(task, 'fr'),
  };
}

export function u3147SessionTaskNeedsEnrich(session: U3147TimeSession): boolean {
  const task = session.task;
  return u3147TaskMetadataNeedsEnrich({
    taskId: session.taskId ?? (task && 'id' in task ? task.id : undefined),
    taskCode: task && 'taskCode' in task ? task.taskCode : undefined,
    taskDescriptionFr: task && 'taskDescriptionFr' in task ? task.taskDescriptionFr : undefined,
    taskDescriptionEn: task && 'taskDescriptionEn' in task ? task.taskDescriptionEn : undefined,
    taskDescription: task && 'description' in task ? task.description : undefined,
  });
}

export function u3147EnrichSessionTask(session: U3147TimeSession, taskList: U3147TaskItem[]): U3147TimeSession {
  if (!u3147SessionTaskNeedsEnrich(session)) return session;

  const rawTaskId = session.taskId?.trim() || session.task?.id?.trim();
  const rawTaskCode = session.task && 'taskCode' in session.task ? session.task.taskCode?.trim() : undefined;

  let resolvedId = u3147ResolveTaskIdForList(rawTaskId, taskList);
  if (!resolvedId && rawTaskCode) {
    const byCode = taskList.find((task) => task.taskCode?.trim() === rawTaskCode);
    if (byCode) resolvedId = String(byCode.id);
  }
  if (!resolvedId) return session;

  const task = taskList.find((entry) => String(entry.id) === String(resolvedId));
  if (!task) return session;

  return {
    ...session,
    taskId: String(task.id),
    task: {
      id: task.id,
      taskCode: task.taskCode,
      taskDescriptionFr: task.taskDescriptionFr,
      taskDescriptionEn: task.taskDescriptionEn,
    },
  };
}

export function u3147EnrichRecentProjectWithTask(
  item: U3147RecentProjectItem,
  task: U3147TaskItem,
): U3147RecentProjectItem {
  return {
    ...item,
    taskCode: task.taskCode,
    taskDescriptionFr: task.taskDescriptionFr,
    taskDescriptionEn: task.taskDescriptionEn,
  };
}

/** Aligne un taskId (session / récent) sur une entrée de findTasksForProject. */
export function u3147ResolveTaskIdForList(
  taskId: string | null | undefined,
  taskList: U3147TaskItem[],
): string {
  const raw = taskId?.trim();
  if (!raw) return '';
  const match = taskList.find((task) => String(task.id) === String(raw));
  return match ? String(match.id) : raw;
}

/**
 * Résout la tâche à appliquer depuis une puce favori + liste findTasksForProject.
 * Si la puce n'a pas taskId, sélectionne l'unique tâche disponible si applicable.
 */
export function u3147ResolveTaskIdForRecentApply(
  item: U3147RecentProjectItem,
  taskList: U3147TaskItem[],
): string {
  const fromTaskId = u3147ResolveTaskIdForList(item.taskId, taskList);
  if (fromTaskId) return fromTaskId;

  const taskCode = item.taskCode?.trim();
  if (taskCode) {
    const byCode = taskList.find((task) => task.taskCode?.trim() === taskCode);
    if (byCode) return String(byCode.id);
  }

  if (taskList.length === 1) {
    return String(taskList[0].id);
  }

  return '';
}

export interface U3147Personalization {
  admIncludeSubProject?: string | boolean | null;
}

/** Personnalisation admin : afficher sous-projet (initLU `personalization.admIncludeSubProject`). */
export function u3147IsAdmIncludeSubProjectEnabled(personalization?: U3147Personalization | null): boolean {
  const v = personalization?.admIncludeSubProject;
  return v === true || v === 'true' || v === '1';
}

export interface U3147InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  roundingMinutes: number;
  organizationTimezone: string;
  employee?: U3147InitLUEmployee | null;
  openSessionId?: string | null;
  personalization?: U3147Personalization;
}

export interface U3147FindProjectItem {
  id: string;
  projetNumber?: string;
  projetName?: string;
  includeSubProjet?: boolean;
  customerCode?: string;
  customerName?: string;
}

export interface U3147FindSubprojectItem {
  id: string;
  projectId: string;
  subProjetNumber?: string;
  subProjetName?: string;
}

export interface U3147FindEmployeeItem {
  id: string;
  codeEmployee?: string;
  firstName?: string;
  lastName?: string;
}

export interface U3147TaskItem {
  id: string;
  taskCode?: string;
  taskDescriptionFr?: string;
  taskDescriptionEn?: string;
}

export interface U3147ProjectRef {
  id: string;
  projetNumber?: string;
  projetName?: string;
  nomClient?: string;
}

export interface U3147SubprojectRef {
  id: string;
  subProjetNumber?: string;
  subProjetName?: string;
}

export interface U3147TaskRef {
  id: string;
  description?: string;
  taskCode?: string;
}

export interface U3147TimeSession {
  id: string;
  version: number;
  employeeId: string;
  projectId: string;
  subprojectId?: string | null;
  taskId?: string | null;
  workDate: string;
  startedAt: string;
  endedAt?: string | null;
  durationMinutes?: number | null;
  source: U3147TimeSessionSource;
  note?: string | null;
  exportedAt?: string | null;
  exportedTimesheetId?: string | null;
  isRunning?: boolean;
  project?: U3147ProjectRef | U3147FindProjectItem;
  subproject?: U3147SubprojectRef | U3147FindSubprojectItem;
  task?: U3147TaskRef | U3147TaskItem;
}

export interface U3147DayResult {
  date: string;
  totalMinutes: number;
  totalHours: number;
  sessions: U3147TimeSession[];
  previousSessionStopped?: boolean;
}

export interface U3147DayRequest {
  date: string;
  employeeId?: string;
}

export interface U3147StartRequest {
  projectId: string;
  subprojectId?: string | null;
  taskId?: string | null;
  note?: string | null;
}

export interface U3147CreateManualRequest {
  projectId: string;
  subprojectId?: string | null;
  taskId?: string | null;
  workDate: string;
  startedAt: string;
  endedAt: string;
  note?: string | null;
}

export interface U3147UpdateRequest extends Partial<U3147CreateManualRequest> {
  id: string;
  version: number;
}

export interface U3147DeleteRequest {
  id: string;
  version: number;
}

export interface U3147ExportRequest {
  date?: string;
  weekMonday?: string;
  employeeId?: string;
}

export interface U3147ExportResult {
  timesheetId: string;
  exportedSessionCount: number;
  warnings?: string[];
}

export interface U3147TimeSessionSearchResult extends U3147TimeSession {
  totalMinutes?: number;
  totalHours?: number;
}

export type U3147AutoCompletCriterias = { fieldName: string; value: unknown }[];

export interface U3147FindTasksForProjectRequest {
  projectId: string;
  subprojectId?: string | null;
}

export const doInitLU = (axios: AxiosInstance) => axios.get(LogicalUnitEndpoints.INITLU);

export const doStart = (axios: AxiosInstance, body: U3147StartRequest) =>
  axios.post(LogicalUnitEndpoints.START, body).then((res) => ({
    ...res,
    data: { result: res.data?.result ?? res.data },
  }));

/** Extrait une session depuis une réponse API U3147 (start/stop/create/update). */
export function u3147UnwrapSessionResponse(res: { data?: unknown }): U3147TimeSession | null {
  const envelope = res.data as { result?: unknown } | undefined;
  const raw = envelope?.result ?? envelope;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const obj = raw as Record<string, unknown>;
  if (typeof obj.id !== 'string' || !obj.id.trim()) return null;
  return obj as unknown as U3147TimeSession;
}

export const doStop = (axios: AxiosInstance, id: string | number) =>
  axios.post(LogicalUnitEndpoints.STOP(id)).then((res) => ({
    ...res,
    data: { result: res.data?.result ?? res.data },
  }));

export const doCreate = (axios: AxiosInstance, body: U3147CreateManualRequest) =>
  axios.post(LogicalUnitEndpoints.CREATE, body).then((res) => ({
    ...res,
    data: { result: res.data?.result ?? res.data },
  }));

export const doUpdate = (axios: AxiosInstance, id: string | number, body: U3147UpdateRequest) =>
  axios.post(LogicalUnitEndpoints.UPDATE(id), body).then((res) => ({
    ...res,
    data: { result: res.data?.result ?? res.data },
  }));

export const doDelete = (axios: AxiosInstance, id: string | number, body: U3147DeleteRequest) =>
  axios.post(LogicalUnitEndpoints.DELETE(id), body);

export const doDay = (axios: AxiosInstance, body: U3147DayRequest) =>
  axios.post(LogicalUnitEndpoints.DAY, body);

export const doSearch = (axios: AxiosInstance, payload: U2000SearchPayload) =>
  axios.post(LogicalUnitEndpoints.SEARCH, payload);

export const doGet = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = forceRefresh ? { b: U2000GenerateUrlUUID() } : undefined;
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doGetAudits = (axios: AxiosInstance, id: string | number) =>
  axios.get(LogicalUnitEndpoints.AUDITS(id), { params: { b: U2000GenerateUrlUUID() } });

export const doExportToTimesheet = (axios: AxiosInstance, body: U3147ExportRequest) =>
  axios.post(LogicalUnitEndpoints.EXPORT_TO_TIMESHEET, body);

export const doFindProject = (axios: AxiosInstance, criterias: U3147AutoCompletCriterias) =>
  axios.post(LogicalUnitEndpoints.FINDPROJECT, criterias);

export const doFindSubproject = (axios: AxiosInstance, criterias: U3147AutoCompletCriterias) =>
  axios.post(LogicalUnitEndpoints.FINDSUBPROJECT, criterias);

export const doFindTasksForProject = (axios: AxiosInstance, body: U3147FindTasksForProjectRequest) =>
  axios.post(LogicalUnitEndpoints.FINDTASKSFORPROJECT, body);

export function u3147FormatProjectLabel(item: U3147FindProjectItem | U3147ProjectRef | undefined | null): string {
  if (!item) return '';
  const num = item.projetNumber?.trim();
  const name = 'projetName' in item ? item.projetName?.trim() : undefined;
  if (num && name) return `${num} — ${name}`;
  return num || name || item.id || '';
}

export function u3147FormatSubprojectLabel(item: U3147FindSubprojectItem | U3147SubprojectRef | undefined | null): string {
  if (!item) return '';
  const num = item.subProjetNumber?.trim();
  const name = item.subProjetName?.trim();
  if (num && name) return `${num} — ${name}`;
  return num || name || item.id || '';
}

export function u3147FormatTaskLabel(item: U3147TaskItem | U3147TaskRef | undefined | null, lang: string): string {
  if (!item) return '';

  let code = item.taskCode?.trim();
  let desc: string | undefined;

  if ('taskDescriptionFr' in item || 'taskDescriptionEn' in item) {
    const fr = item.taskDescriptionFr?.trim();
    const en = item.taskDescriptionEn?.trim();
    desc = lang.startsWith('fr') ? fr || en : en || fr;
  }

  if ('description' in item) {
    const stored = item.description?.trim();
    if (stored) {
      if (stored.includes(' — ')) {
        const [storedCode, ...rest] = stored.split(' — ');
        const storedDesc = rest.join(' — ').trim();
        if (!code) code = storedCode.trim();
        if (storedDesc && storedDesc !== storedCode.trim()) {
          desc = desc || storedDesc;
        } else if (!desc && storedDesc !== code) {
          return stored;
        }
      } else if (!desc && stored !== code && stored !== item.id) {
        desc = stored;
      } else if (!code) {
        code = stored;
      }
    }
  }

  if (code && desc) {
    if (code === desc) return code;
    return `${code} — ${desc}`;
  }
  return desc || code || item.id || '';
}

export function u3147IsSessionExported(session: U3147TimeSession): boolean {
  return !!(session.exportedAt?.trim() || session.exportedTimesheetId?.trim());
}

export function u3147IsSessionRunning(session: U3147TimeSession): boolean {
  if (session.isRunning === true) return true;
  return session.endedAt == null || session.endedAt === '';
}

export function luHandleApiError(t: TranslateFn, NS: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; errorType?: string; description?: string };
  const desc = apiErr?.description?.trim();

  if (apiErr?.errorType === 'EmployeeNotConfigured' || desc?.includes('administrateur')) {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: NS }),
      text: t('msgEmployeeNotConfigured', { ns: NS, defaultValue: 'Contactez votre administrateur.' }),
    });
    return;
  }
  if (apiErr?.errorType === 'TimesheetAccessDenied' || desc?.includes('Accès feuille de temps')) {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: NS }),
      text: t('msgTimesheetAccessDenied', {
        ns: NS,
        defaultValue: 'Accès feuille de temps refusé pour ce projet.',
      }),
    });
    return;
  }
  if (apiErr?.errorType === 'SessionAlreadyExported' || desc?.includes('Plage déjà exportée')) {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: NS }),
      text: t('msgSessionAlreadyExported', { ns: NS, defaultValue: 'Plage déjà exportée.' }),
    });
    return;
  }
  if (desc?.includes('exportée vers la feuille de temps') && desc?.includes('suppression')) {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: NS }),
      text: t('msgSessionDeleteExported', {
        ns: NS,
        defaultValue: 'Cette plage a déjà été exportée vers la feuille de temps ; suppression impossible.',
      }),
    });
    return;
  }
  if (desc?.includes('supprimer une session en cours') || desc?.includes('arrêtez le chronomètre')) {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: NS }),
      text: t('msgSessionDeleteRunning', {
        ns: NS,
        defaultValue: 'Impossible de supprimer une session en cours ; arrêtez le chronomètre d\'abord.',
      }),
    });
    return;
  }
  if (desc?.includes('Session de temps non trouvée')) {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: NS }),
      text: t('msgSessionNotFound', { ns: NS, defaultValue: 'Session de temps non trouvée.' }),
    });
    return;
  }
  if (apiErr?.errorType === 'TimesheetLocked' || desc?.includes('Feuille de temps non modifiable')) {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: NS }),
      text: t('msgExportLocked', { ns: NS, defaultValue: 'Feuille de temps non modifiable.' }),
    });
    return;
  }
  if (apiErr?.errorType === 'SessionRunning' || desc?.includes('session en cours')) {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: NS }),
      text: t('msgSessionRunning', { ns: NS, defaultValue: 'Impossible de modifier une session en cours.' }),
    });
  }
}
